// Defensive watchdog for stuck `isStreaming=true` state. Defect #3 of the
// 2026-06-02 click-only e2e audit surfaced an empirically-observed case
// where the answer fully streamed but the Composer Stop button +
// AgentTimeline pulse remained in-flight indefinitely. The chain audit
// (chatStore.case 'complete' → Composer / AgentTimeline) was structurally
// correct IF the `complete` event arrives; the only way the stuck state
// persists is either (a) the SSE `complete` event was lost (worker bug
// not redeployable because rag-query is bake-frozen per ADR-092 Phase 2
// through 2026-06-07), (b) the SSE relay closed before forwarding
// `complete` (transport race), or (c) React batching delays the
// re-render past observable horizons.
//
// This hook closes the gap from the chat-ui side: track `lastEventAt`
// in chatStore (updated by applyEvent on every event + startAssistantMessage
// at stream start), poll every 5s while isStreaming is true, and force
// endStream() if no event has arrived for > 60s. The 60s threshold is
// tuned to be just past gpt-5.4 medium-effort silent reasoning windows
// (60-90s per AGENTS.md #6 narrative) AND comfortably catches operator-
// perceived stuck state from SSE complete-event propagation latency
// (~30-50s observed empirically across 2026-06-02 sessions). Trade-off:
// legitimately long silent reasoning could false-positive endStream;
// operator preference per 2026-06-02 feedback prioritizes faster UX
// recovery over preserving rare long-reasoning streams. The original
// 120s threshold was structurally generous but produced a 30-50s gap
// of operator-visible "stuck Stop button" after the answer was already
// done streaming, which the operator empirically described as "long time".
//
// A `console.warn` is emitted on every watchdog fire for operator forensics
// — when the operator next reports a stuck state, the warning logs the
// silence duration + activeJobId so the failure can be correlated against
// the rag-query worker logs server-side.

import { useEffect } from "react";
import { useChatStore } from "./chatStore";

const SILENCE_THRESHOLD_MS = 60_000;
const CHECK_INTERVAL_MS = 5_000;

export function useStreamWatchdog(): void {
  const isStreaming = useChatStore((s) => s.isStreaming);
  useEffect(() => {
    if (!isStreaming) return;
    const interval = window.setInterval(() => {
      const state = useChatStore.getState();
      if (!state.isStreaming) return;
      const last = state.lastEventAt;
      if (last === null) return;
      const silenceMs = Date.now() - last;
      if (silenceMs > SILENCE_THRESHOLD_MS) {
        // eslint-disable-next-line no-console
        console.warn(
          `[stream-watchdog] silent for ${(silenceMs / 1000).toFixed(0)}s — forcing endStream`,
          { lastEventAt: last, activeJobId: state.activeJobId },
        );
        state.endStream();
      }
    }, CHECK_INTERVAL_MS);
    return () => window.clearInterval(interval);
  }, [isStreaming]);
}
